/** Router components: in-app links and declarative redirects. */
import { useEffect, type AnchorHTMLAttributes, type MouseEvent, type Ref } from 'react';
import { navigate, resolveHref } from './router';

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  replace?: boolean;
  ref?: Ref<HTMLAnchorElement>;
}

/** In-app link: a real <a href> (so it works with middle-click/copy) that navigates client-side. */
export function Link({ to, replace, onClick, target, ref, children, ...rest }: LinkProps) {
  const href = resolveHref(to);
  return (
    <a
      {...rest}
      ref={ref}
      href={href}
      target={target}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          (target && target !== '_self')
        ) {
          return;
        }
        event.preventDefault();
        navigate(to, { replace });
      }}
    >
      {children}
    </a>
  );
}

/** Declarative redirect (replace) rendered by route tables. */
export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}
